import { Box, SxProps, Tab as MuiTab, Tabs as MuiTabs, useTheme } from '@mui/material';
import { useRef, useEffect, useCallback } from 'react';
import { useLocale } from '@repo/i18n';
import { route } from 'src/components/common/router/Router';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';

type Tab = {
    name: string;
    // Stable identity for React keys. Checkout tab names embed the step number, which can renumber
    // while a tab stays mounted (e.g. the member-card step appearing/disappearing) — keying by name
    // would then remount the active panel, destroying stateful content like the Nets payment iframe.
    identifier?: string;
    content: React.ReactNode[] | React.ReactNode;
    disabled?: boolean;
    sx?: SxProps;
};
type TabSet = {
    ordered?: boolean;
    tabs: Tab[];
};

type TabsProps = {
    tabset: TabSet;
    currentTab: number;
    onChange: (index: number) => void;
};

function a11yProps(index: number) {
    return {
        id: `tab-${index}`,
        'aria-controls': `tabpanel-${index}`,
    };
}

export default function Tabs({ tabset: { tabs, ordered }, currentTab, onChange }: TabsProps) {
    const theme = useTheme();
    const { t } = useLocale();
    const config = useConfigurations();
    const customizations = useCustomizations();

    const tabPanelRef = useRef<HTMLDivElement>(null);
    const tabsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Focus the tabpanel on mount so screen readers announce the new view
        tabPanelRef.current?.focus();
    }, []);

    const headerSize = `${50 + 48 + 8 * 2}px`;

    const handleChangeTab = (_e: any, value: number) => {
        onChange(value);
    };

    const isTabDisabled = useCallback(
        (index: number) => {
            const tab = tabs[index];
            return tab?.disabled || (ordered && currentTab < index);
        },
        [tabs, ordered, currentTab],
    );

    // WAI-ARIA keyboard navigation for tabs
    // MUI's built-in keyboard navigation doesn't work inside Shadow DOM because it uses
    // document.activeElement which returns the shadow host, not the actual focused element.
    // We implement our own navigation following the WAI-ARIA tabs pattern.
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            const { key } = event;
            let newIndex: number | null = null;

            if (key === 'ArrowRight' || key === 'ArrowLeft') {
                event.preventDefault();
                const direction = key === 'ArrowRight' ? 1 : -1;
                let nextIndex = currentTab + direction;

                // Find next enabled tab, wrapping around
                const startIndex = nextIndex;
                while (true) {
                    // Wrap around
                    if (nextIndex >= tabs.length) nextIndex = 0;
                    if (nextIndex < 0) nextIndex = tabs.length - 1;

                    if (!isTabDisabled(nextIndex)) {
                        newIndex = nextIndex;
                        break;
                    }

                    nextIndex += direction;

                    // Prevent infinite loop if all tabs are disabled
                    if (nextIndex === startIndex) break;
                }
            } else if (key === 'Home') {
                event.preventDefault();
                // Find first enabled tab
                for (let i = 0; i < tabs.length; i++) {
                    if (!isTabDisabled(i)) {
                        newIndex = i;
                        break;
                    }
                }
            } else if (key === 'End') {
                event.preventDefault();
                // Find last enabled tab
                for (let i = tabs.length - 1; i >= 0; i--) {
                    if (!isTabDisabled(i)) {
                        newIndex = i;
                        break;
                    }
                }
            }

            if (newIndex !== null && newIndex !== currentTab) {
                onChange(newIndex);
                // Focus the new tab button
                const tabButton = tabsRef.current?.querySelector(`#tab-${newIndex}`) as HTMLElement;
                tabButton?.focus();
            }
        },
        [currentTab, tabs.length, isTabDisabled, onChange],
    );

    const logoString = config.logo !== '' ? config.logo : customizations.iconUrl;

    const tabToRender = tabs[currentTab];

    return (
        <Box sx={{ marginBottom: theme.spacing(15) }}>
            <Box
                sx={{
                    position: 'relative',
                    backgroundColor: customizations.checkoutHeaderColor,
                    color: customizations.checkoutHeaderColorContrast,
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    height: headerSize,
                    '& .MuiTabs-flexContainer': {
                        borderBottom: customizations.navigationTabBorderBottom,
                    },

                    [theme.breakpoints.up('md')]: {
                        flexDirection: 'row',
                        alignItems: 'flex-end',
                        '& img': {
                            marginLeft: theme.spacing(3),
                        },
                    },
                    [theme.breakpoints.down('md')]: {
                        '& img': {
                            marginTop: theme.spacing(2),
                        },
                    },
                    [theme.breakpoints.up('lg')]: {
                        flexDirection: 'row',
                        height: null,

                        '& img': {
                            position: 'absolute',
                            marginLeft: theme.spacing(3),
                        },
                    },
                    [theme.breakpoints.down('lg')]: {
                        '& img': {
                            height: theme.spacing(6.25),
                            marginLeft: theme.spacing(2),
                        },
                    },
                }}
            >
                {logoString && (
                    <img
                        onClick={() => route('/')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                route('/');
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={t.aria_go_to_home}
                        src={logoString}
                        alt=""
                        style={{
                            cursor: 'pointer',
                            alignSelf: 'center',
                            maxHeight: theme.spacing(10),
                        }}
                    />
                )}
                <Box component="nav" aria-label={t.aria_checkout_steps} sx={{ width: '100%' }}>
                    <MuiTabs
                        ref={tabsRef}
                        value={currentTab}
                        onChange={handleChangeTab}
                        onKeyDown={handleKeyDown}
                        variant="scrollable"
                        scrollButtons={false}
                        textColor="inherit"
                        sx={{
                            color: customizations.checkoutHeaderColorContrast,
                            margin: '0 auto',
                            maxWidth: `calc(960px + ${theme.spacing(2)})`,
                            overflowX: 'auto',
                            width: '100%',
                            '& .Mui-selected': {
                                color: customizations.checkoutHeaderPrimaryColor,
                            },
                            '& .Mui-disabled': {
                                color: customizations.checkoutHeaderColorContrast,
                                opacity: 0.5,
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: customizations.checkoutHeaderPrimaryColor,
                            },
                            '& .MuiTabs-flexContainer': {
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                                maxWidth: `calc(960px + ${theme.spacing(2)})`,
                                overflowX: 'auto',
                            },
                            [`@media screen and (max-width: calc(1450px + ${theme.spacing(2)}))`]: {
                                maxWidth: `calc(850px + ${theme.spacing(2)})`,
                            },
                            '@media print': {
                                '& .no-print': {
                                    display: 'none',
                                },
                            },
                        }}
                    >
                        {tabs.map((tab, i) => (
                            <MuiTab
                                key={`tab-header-${tab.identifier ?? tab.name}`}
                                className="no-print"
                                sx={{
                                    opacity: 1.0,
                                }}
                                disabled={isTabDisabled(i)}
                                label={tab.name}
                                {...a11yProps(i)}
                            />
                        ))}
                    </MuiTabs>
                </Box>
            </Box>
            <Box position="relative" m="0 auto" maxWidth={`calc(1100px + ${theme.spacing(2)})`}>
                {tabToRender && (
                    <Box
                        ref={tabPanelRef}
                        tabIndex={-1}
                        key={`tab-panel-${tabToRender.identifier ?? tabToRender.name}`}
                        role="tabpanel"
                        id={`tabpanel-${currentTab}`}
                        aria-labelledby={`tab-${currentTab}`}
                    >
                        <Box
                            sx={{
                                px: theme.spacing(1),
                                mt: theme.spacing(4),
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            {tabToRender.content}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export { Tab, TabSet, TabsProps };
